/**
 * Control plane ⇄ VPS agent protocol, version 1.
 *
 * The agent opens an outbound WebSocket to `{serverUrl}/agent/v1/connect` with
 * `Authorization: Bearer <agent token>` and `X-DevRobot-Agent-Id: <agent id>`.
 * Every frame is a JSON text message with a `type` discriminator.
 * The normative description lives in docs/architecture/agent-protocol.md; the Go
 * agent (agent/internal/protocol) mirrors these types.
 */

export const AGENT_PROTOCOL_VERSION = 1;
export const EDGE_NETWORK = 'devrobot-edge';

/* ------------------------------------------------------------ observations */

export interface Capacity {
  cpus: number;
  memoryBytes: number;
  memoryAvailableBytes: number;
  diskFreeBytes: number;
  diskTotalBytes: number;
}

export interface ServiceObservation {
  service: string;
  containerId: string;
  containerName: string;
  state: 'running' | 'exited' | 'restarting' | 'created' | 'paused' | 'dead' | 'removing';
  health: 'healthy' | 'unhealthy' | 'starting' | 'none';
  image: string;
  startedAt?: string;
  exitCode?: number;
  /** A setup service expected to run once and exit; exit code 0 means completed. */
  runOnce?: boolean;
}

export interface StackObservation {
  stack: string;
  services: ServiceObservation[];
  volumes: string[];
  /** Release the agent believes is applied (commit SHA), if known. */
  commitSha?: string;
  configHash?: string;
}

export interface RouteObservation {
  stack: string;
  hostname: string;
  certificate: 'valid' | 'pending' | 'failed' | 'unknown';
  error?: string;
}

export interface EdgeObservation {
  mode: 'managed-caddy' | 'none' | 'blocked';
  running: boolean;
  httpPort: number;
  httpsPort: number;
  routes: RouteObservation[];
  error?: string;
}

export interface HostInfo {
  hostname: string;
  os: string;
  osVersion: string;
  kernel: string;
  arch: string;
}

/* --------------------------------------------------------- agent → server */

export interface JobResultMessage {
  type: 'job.result';
  jobId: string;
  ok: boolean;
  /** True once the task changed anything on the host (containers, files, routes, volumes). */
  mutated: boolean;
  error?: { code: string; message: string; stage?: string };
  data?: Record<string, unknown>;
}

export type AgentMessage =
  | {
      type: 'hello';
      protocol: number;
      agentId: string;
      version: string;
      driver: 'docker' | 'simulate';
      host: HostInfo;
      docker: { version: string; composeVersion: string } | null;
      capacity: Capacity;
      activeJobs: string[];
      pendingResults: JobResultMessage[];
      /** Optional features this agent offers (AGENT_CAPABILITIES); absent on older agents. */
      capabilities?: string[];
      /** Public host keys of the local sshd (`ssh-ed25519 AAAA…`), used to pin native SSH sessions. */
      sshHostKeys?: string[];
    }
  | { type: 'heartbeat'; ts: string; capacity: Capacity; stacks: StackObservation[]; edge: EdgeObservation }
  | { type: 'job.accepted'; jobId: string }
  | { type: 'job.stage'; jobId: string; stage: string; state: 'running' | 'succeeded' | 'failed' | 'skipped'; message?: string }
  | { type: 'job.log'; jobId: string; stage: string; level: 'debug' | 'info' | 'warn' | 'error'; message: string; ts: string }
  | JobResultMessage
  | { type: 'response'; requestId: string; ok: boolean; data?: unknown; error?: { code: string; message: string } };

/* --------------------------------------------------------- server → agent */

export interface PrivateFileDelivery {
  name: string;
  destination: string;
  mode: string;
  uid?: number;
  gid?: number;
  contentBase64: string;
  sha256: string;
}

export interface ServicePlan {
  environment: Record<string, string>;
  /** Names of keys in `environment` whose values are secret (agent masks them in logs). */
  secretKeys: string[];
  files: PrivateFileDelivery[];
  resources?: { memory?: string; cpus?: number };
}

export interface PortBinding {
  service: string;
  containerPort: number;
  hostPort: number;
  protocol: 'tcp' | 'udp';
  bindAddress: string;
}

export interface RouteBinding {
  service: string;
  containerPort: number;
  hostname: string;
}

export interface HealthPlan {
  service: string;
  type: 'http' | 'tcp' | 'docker';
  path: string;
  port?: number;
  timeoutSeconds: number;
  intervalSeconds: number;
}

export interface DeployPlan {
  projectKey: string;
  environment: string;
  targetId: string;
  release: { commitSha: string; branch: string };
  /** Deployment files fetched from GitLab at the pinned commit (Compose files and what they reference). */
  files: { path: string; contentBase64: string }[];
  composeFiles: string[];
  profiles: string[];
  /** service → image reference pinned by digest (`registry/group/project/service@sha256:…`). */
  images: Record<string, string>;
  registry: { host: string; username: string; password: string } | null;
  services: Record<string, ServicePlan>;
  ports: PortBinding[];
  routes: RouteBinding[];
  health: HealthPlan | null;
  migrations: { service: string; command: string[] } | null;
  volumes: string[];
  keepRunningOnStop: string[];
  /**
   * Declared setup services (manifest `container.runOnce`). The agent adds services that others
   * wait for with `condition: service_completed_successfully`.
   */
  runOnce: string[];
  configHash: string;
  /**
   * Services created but NOT started by this deploy (data migration: the destination waits for
   * its data). When non-empty the agent skips migrations and health verification. Only sent to
   * agents announcing `migrate.v1`.
   */
  holdServices?: string[];
}

export type AgentTask =
  | { kind: 'deploy'; stack: string; mode: 'deploy' | 'apply-config' | 'rollback'; plan: DeployPlan }
  | { kind: 'lifecycle'; stack: string; action: 'start' | 'stop' | 'restart'; services: string[] }
  | {
      kind: 'remove';
      stack: string;
      removeImages: boolean;
      removeFiles: boolean;
      removeNetworks: boolean;
    }
  | { kind: 'delete-data'; stack: string; volumes: string[] }
  | {
      kind: 'backup';
      stack: string;
      backupId: string;
      database: { service: string; type: string; user: string; database?: string };
      uploadUrl: string;
    }
  | {
      kind: 'restore';
      stack: string;
      backupId: string;
      database: { service: string; type: string; user: string; database?: string };
      downloadUrl: string;
      sha256: string;
      stopServices: string[];
      startServices: string[];
    }
  /** Copies one Compose volume of the stack into an archive and uploads it (data migration). */
  | {
      kind: 'volume-export';
      stack: string;
      artifactId: string;
      /** Volume name as declared in the manifest; the Docker volume is `${stack}_${volume}`. */
      volume: string;
      /** Services stopped before archiving; they are restarted afterwards even on failure. */
      stopServices: string[];
      restartServices: string[];
      uploadUrl: string;
    }
  /** Downloads a volume archive and unpacks it into a Compose volume of the stack. Never starts services. */
  | {
      kind: 'volume-import';
      stack: string;
      artifactId: string;
      volume: string;
      downloadUrl: string;
      sha256: string;
      /** Replace existing contents; without it a non-empty volume fails with `destination_not_empty`. */
      overwrite: boolean;
      stopServices: string[];
    }
  | { kind: 'agent-upgrade'; version: string; url: string; sha256: string }
  /** Trusts the DevRobot SSH user CA for `user` when a certificate names `principal` (native SSH). */
  | { kind: 'ssh-ca-install'; caPublicKey: string; principal: string; user: string }
  | { kind: 'ssh-ca-remove' };

export type AgentRequestAction = 'observe' | 'preflight' | 'logs' | 'check-network' | 'inspect-host';

export interface PreflightParams {
  stack: string;
  ports: { hostPort: number; protocol: 'tcp' | 'udp' }[];
  containerNames: string[];
  volumes: string[];
  needsEdge: boolean;
}

export interface PreflightResult {
  ports: { hostPort: number; protocol: string; free: boolean; ownedByStack: boolean; owner?: string }[];
  containerNames: { name: string; exists: boolean; ownedByStack: boolean }[];
  volumes: { name: string; exists: boolean }[];
  diskFreeBytes: number;
  edge: EdgeObservation;
}

export interface LogsParams {
  stack: string;
  services: string[];
  tail: number;
  since?: string;
}

export interface LogsResult {
  lines: { service: string; ts: string; message: string }[];
}

export type ServerMessage =
  | { type: 'welcome'; agentId: string; serverTime: string; heartbeatSeconds: number }
  | { type: 'job.run'; jobId: string; task: AgentTask }
  | { type: 'job.cancel'; jobId: string }
  | { type: 'job.ack'; jobId: string }
  | { type: 'request'; requestId: string; action: AgentRequestAction; params: unknown }
  | TunnelOpenMessage;

/* ----------------------------------------------------------------- tunnels */

/** Optional agent features announced in `hello.capabilities`. */
export const AGENT_CAPABILITIES = {
  /** Interactive host shell (pty tunnels). */
  tunnel: 'tunnel.v1',
  /** Byte tunnels to the local sshd for native SSH clients. */
  tunnelSsh: 'tunnel.tcp-ssh',
  /** Installs and removes the DevRobot SSH user CA (jobs `ssh-ca-install` / `ssh-ca-remove`). */
  sshCa: 'ssh-ca.v1',
  /** Data migration: `volume-export` / `volume-import` jobs and `DeployPlan.holdServices`. */
  migrate: 'migrate.v1',
} as const;

export type TunnelKind = 'pty' | 'tcp';

/**
 * Asks the agent to dial `{serverUrl}/agent/v1/tunnels/{tunnelId}` (same bearer identity as the
 * main connection) and attach a shell (`pty`) or the local sshd (`tcp`) to that socket.
 * The URL is derived from the agent's configured server, never taken from the message.
 */
export interface TunnelOpenMessage {
  type: 'tunnel.open';
  tunnelId: string;
  kind: TunnelKind;
  cols?: number;
  rows?: number;
  /** Runs this command through `sh -lc` instead of a login shell (pty only). */
  command?: string;
}

/**
 * Control frames on a tunnel socket (JSON text frames). Binary frames carry raw terminal or TCP bytes.
 * client → agent: resize. agent → client: ready, exit, error. server → client: close.
 */
export type TunnelControlFrame =
  | { t: 'resize'; cols: number; rows: number }
  | { t: 'ready' }
  | { t: 'exit'; code: number }
  | { t: 'error'; message: string }
  | { t: 'close'; reason: TunnelEndReason; message: string; exitCode?: number | null };

/** Why a shell or SSH session ended (stored on the session and sent in the final `close` frame). */
export type TunnelEndReason =
  | 'exited'
  | 'client-closed'
  | 'agent-closed'
  | 'agent-refused'
  | 'agent-timeout'
  | 'agent-disconnected'
  | 'ticket-expired'
  | 'idle-timeout'
  | 'max-duration'
  | 'access-revoked'
  | 'terminated'
  | 'server-shutdown'
  | 'interrupted';

/* ------------------------------------------------------------ enrollment */

/** Output of `devrobot-agent inspect --json`, used during enrollment (FR-VPS-01 step 3). */
export interface InspectionReport {
  agentVersion: string;
  hostname: string;
  os: { id: string; versionId: string; prettyName: string; kernel: string };
  arch: string;
  isRoot: boolean;
  systemd: boolean;
  docker: { installed: boolean; version?: string; serverReachable: boolean; error?: string };
  compose: { installed: boolean; version?: string; supportsOverrideTags: boolean };
  resources: Capacity & { dataDir: string };
  containers: { id: string; name: string; image: string; state: string; ports: string; composeProject?: string; devrobotStack?: string }[];
  edge: {
    httpPort: number;
    httpsPort: number;
    httpOwner: string;
    httpsOwner: string;
  };
  existingAgent: { installed: boolean; agentId?: string; version?: string; serverUrl?: string };
  /** `remedy` marks a blocker that enrollment fixes itself after approval (install_docker: Docker is installed). */
  problems: { code: string; severity: 'blocker' | 'warning'; message: string; remedy?: 'install_docker' }[];
}
